import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Modal, ScrollView, TouchableWithoutFeedback, TextInput } from 'react-native';
import { Phone, Send, Globe, CheckCircle2, XCircle, Clock, MessageCircle, ChevronDown, ChevronUp, Building, Play } from '../lib/icons';
import { shadows } from '../lib/styles';

export type ProviderStatus = 'pending' | 'contacted' | 'in_conversation' | 'unreachable' | 'responded' | 'declined';
export type OutreachOwner = 'human' | 'agent';

export type ContactMethod = 'phone' | 'text' | 'email' | 'web_form';

export interface ContactAttempt {
  id: string;
  method: ContactMethod;
  timestamp: string;
  status: 'success' | 'failed' | 'no_response';
  notes?: string;
}

export interface OutreachProvider {
  id: string;
  name: string;
  specialty: string;
  reason?: string;
  status: ProviderStatus;
  contact_progress?: ContactAttempt[];
  last_contacted_at?: string;
  suggested_contact_method?: ContactMethod; // AI's recommended method
  contact_method?: ContactMethod; // Final method (human override or AI suggestion)
  contact_method_reason?: string; // Reason for the selected method
  outreach_owner?: OutreachOwner; // Who is responsible for outreach
}

interface OutreachProviderListProps {
  providers: OutreachProvider[];
  onProviderStatusChange?: (providerId: string, status: ProviderStatus) => void;
  onContactProgressChange?: (providerId: string, contactProgress: ContactAttempt[]) => void;
  onContactMethodChange?: (providerId: string, method: ContactMethod) => void;
  onOutreachOwnerChange?: (providerId: string, owner: OutreachOwner) => void;
  onRunAgentOutreach?: (providerId: string) => void;
  disabled?: boolean;
}

const STATUS_CONFIG: Record<ProviderStatus, { label: string; color: string; icon: any }> = {
  pending: { label: 'Not Contacted', color: '#9CA3AF', icon: Clock },
  contacted: { label: 'Contacted', color: '#2563EB', icon: CheckCircle2 },
  in_conversation: { label: 'In Conversation', color: '#F59E0B', icon: MessageCircle },
  unreachable: { label: "Can't Reach", color: '#DC2626', icon: XCircle },
  responded: { label: 'Responded', color: '#16A34A', icon: CheckCircle2 },
  declined: { label: 'Declined', color: '#6B7280', icon: XCircle },
};

const CONTACT_METHOD_CONFIG: Record<ContactMethod, { label: string; icon: any; color: string }> = {
  text: { label: 'Text/SMS', icon: MessageCircle, color: '#8B5CF6' },
  phone: { label: 'Phone Call', icon: Phone, color: '#2563EB' },
  email: { label: 'Email', icon: Send, color: '#16A34A' },
  web_form: { label: 'Web Form', icon: Globe, color: '#F59E0B' },
};

const OUTREACH_OWNER_CONFIG: Record<OutreachOwner, { label: string; color: string }> = {
  human: { label: 'Human', color: '#2563EB' },
  agent: { label: 'AI Agent', color: '#7C3AED' },
};

function getValidContactMethod(method: string | undefined | null): ContactMethod | null {
  if (!method || !(method in CONTACT_METHOD_CONFIG)) return null;
  return method as ContactMethod;
}

/** Prefer a recognized value: first valid among contact_method, then suggested (handles invalid contact_method shadowing a valid suggestion). */
function resolveEffectiveContactMethod(provider: OutreachProvider): ContactMethod | null {
  return getValidContactMethod(provider.contact_method) ?? getValidContactMethod(provider.suggested_contact_method);
}

export const OutreachProviderList: React.FC<OutreachProviderListProps> = ({
  providers,
  onProviderStatusChange,
  onContactProgressChange,
  onContactMethodChange,
  onOutreachOwnerChange,
  onRunAgentOutreach,
  disabled = false,
}) => {
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [contactMethodModalVisible, setContactMethodModalVisible] = useState(false);
  const [selectedContactProviderId, setSelectedContactProviderId] = useState<string | null>(null);
  const [outreachOwnerModalVisible, setOutreachOwnerModalVisible] = useState(false);
  const [selectedOwnerProviderId, setSelectedOwnerProviderId] = useState<string | null>(null);
  const [logAttemptModalVisible, setLogAttemptModalVisible] = useState(false);
  const [selectedLogProviderId, setSelectedLogProviderId] = useState<string | null>(null);
  const [logMethod, setLogMethod] = useState<ContactMethod>('phone');
  const [logStatus, setLogStatus] = useState<'success' | 'failed' | 'no_response'>('success');
  const [logNotes, setLogNotes] = useState('');

  const handleStatusChange = (providerId: string, newStatus: ProviderStatus) => {
    if (onProviderStatusChange) {
      onProviderStatusChange(providerId, newStatus);
    }
    setStatusModalVisible(false);
    setSelectedProviderId(null);
  };

  const openStatusModal = (providerId: string) => {
    if (disabled || !onProviderStatusChange) return;
    setSelectedProviderId(providerId);
    setStatusModalVisible(true);
  };

  const handleContactMethodChange = (providerId: string, newMethod: ContactMethod) => {
    if (onContactMethodChange) {
      onContactMethodChange(providerId, newMethod);
    }
    setContactMethodModalVisible(false);
    setSelectedContactProviderId(null);
  };

  const openContactMethodModal = (providerId: string) => {
    if (disabled || !onContactMethodChange) return;
    setSelectedContactProviderId(providerId);
    setContactMethodModalVisible(true);
  };

  const handleOutreachOwnerChange = (providerId: string, owner: OutreachOwner) => {
    if (onOutreachOwnerChange) {
      onOutreachOwnerChange(providerId, owner);
    }
    setOutreachOwnerModalVisible(false);
    setSelectedOwnerProviderId(null);
  };

  const openOutreachOwnerModal = (providerId: string) => {
    if (disabled || !onOutreachOwnerChange) return;
    setSelectedOwnerProviderId(providerId);
    setOutreachOwnerModalVisible(true);
  };

  const openLogAttemptModal = (provider: OutreachProvider) => {
    const owner: OutreachOwner = provider.outreach_owner === 'agent' ? 'agent' : 'human';
    if (disabled || !onContactProgressChange || owner !== 'human') return;
    setSelectedLogProviderId(provider.id);
    setLogMethod(resolveEffectiveContactMethod(provider) || 'phone');
    setLogStatus('success');
    setLogNotes('');
    setLogAttemptModalVisible(true);
  };

  const handleSubmitLogAttempt = () => {
    if (!selectedLogProviderId || !onContactProgressChange) return;
    const provider = providers.find((entry) => entry.id === selectedLogProviderId);
    if (!provider) return;

    const existingProgress = provider.contact_progress || [];
    const newAttempt: ContactAttempt = {
      id: `human-attempt-${Date.now()}-${provider.id}`,
      method: logMethod,
      timestamp: new Date().toISOString(),
      status: logStatus,
      notes: logNotes.trim() || 'Logged by team member',
    };

    onContactProgressChange(selectedLogProviderId, [...existingProgress, newAttempt]);
    setLogAttemptModalVisible(false);
    setSelectedLogProviderId(null);
    setLogNotes('');
  };

  const selectedProvider = selectedProviderId ? providers.find(p => p.id === selectedProviderId) : null;
  const selectedStatusConfig = selectedProvider
    ? selectedProvider.status in STATUS_CONFIG
      ? STATUS_CONFIG[selectedProvider.status]
      : { label: 'Unknown status', color: '#9CA3AF', icon: Clock }
    : null;
  
  const selectedContactProvider = selectedContactProviderId ? providers.find(p => p.id === selectedContactProviderId) : null;
  const selectedOwnerProvider = selectedOwnerProviderId ? providers.find(p => p.id === selectedOwnerProviderId) : null;
  const selectedLogProvider = selectedLogProviderId ? providers.find((provider) => provider.id === selectedLogProviderId) : null;

  if (providers.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Building size={24} color="#9CA3AF" />
        <Text style={styles.emptyStateText}>No providers to contact</Text>
        <Text style={styles.emptyStateSubtext}>Complete the Plan stage to identify providers</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Building size={16} color="#2563EB" />
        <Text style={styles.headerText}>
          Service Providers ({providers.length})
        </Text>
      </View>

      <View style={styles.providersList}>
        {providers.map((provider) => {
          const statusConfig =
            provider.status in STATUS_CONFIG
              ? STATUS_CONFIG[provider.status]
              : { label: 'Unknown status', color: '#9CA3AF', icon: Clock };
          const StatusIcon = statusConfig.icon;
          const contactProgress = provider.contact_progress || [];
          const hasProgress = contactProgress.length > 0;
          const outreachOwner: OutreachOwner = provider.outreach_owner === 'agent' ? 'agent' : 'human';
          const outreachOwnerConfig = OUTREACH_OWNER_CONFIG[outreachOwner];
          const validContactMethod = getValidContactMethod(provider.contact_method);
          const validSuggestedMethod = getValidContactMethod(provider.suggested_contact_method);
          const effectiveContactMethod = resolveEffectiveContactMethod(provider);
          const showAiSuggestedRow =
            !!validSuggestedMethod &&
            !!validContactMethod &&
            validSuggestedMethod !== validContactMethod;
          const canUpdateStatus = outreachOwner === 'human' && !disabled && !!onProviderStatusChange;
          const canRunAgentOutreach = outreachOwner === 'agent' && !disabled && !!onRunAgentOutreach;
          const canLogHumanAttempt = outreachOwner === 'human' && !disabled && !!onContactProgressChange;

          return (
            <View key={provider.id} style={styles.providerCard}>
              <View style={styles.providerHeader}>
                <View style={styles.providerInfo}>
                  <Text style={styles.providerName}>{provider.name}</Text>
                  <Text style={styles.providerSpecialty}>{provider.specialty}</Text>
                  {provider.reason && (
                    <Text style={styles.providerReason}>{provider.reason}</Text>
                  )}
                </View>
                <Pressable
                  style={[styles.statusButton, { backgroundColor: `${statusConfig.color}15` }]}
                  onPress={() => canUpdateStatus && openStatusModal(provider.id)}
                  disabled={!canUpdateStatus}
                >
                  <StatusIcon size={14} color={statusConfig.color} />
                  <Text style={[styles.statusText, { color: statusConfig.color }]}>
                    {statusConfig.label}
                  </Text>
                </Pressable>
              </View>

              <View style={styles.contactMethodSection}>
                <View style={styles.contactMethodRow}>
                  <Text style={styles.contactMethodLabel}>Outreach by:</Text>
                  <Pressable
                    style={[styles.contactMethodBadge, { backgroundColor: `${outreachOwnerConfig.color}15` }]}
                    onPress={() => openOutreachOwnerModal(provider.id)}
                    disabled={disabled || !onOutreachOwnerChange}
                  >
                    <Text style={[styles.contactMethodText, { color: outreachOwnerConfig.color }]}>
                      {outreachOwnerConfig.label}
                    </Text>
                    {onOutreachOwnerChange && !disabled && (
                      <ChevronDown size={12} color={outreachOwnerConfig.color} />
                    )}
                  </Pressable>
                </View>
                {outreachOwner === 'agent' && (
                  <View style={styles.agentActionsRow}>
                    <Text style={styles.agentHelpText}>
                      Run AI outreach when ready.
                    </Text>
                    <Pressable
                      style={[styles.agentGoButton, !canRunAgentOutreach && styles.agentGoButtonDisabled]}
                      onPress={() => canRunAgentOutreach && onRunAgentOutreach(provider.id)}
                      disabled={!canRunAgentOutreach}
                    >
                      <Play size={12} color="#FFFFFF" />
                      <Text style={styles.agentGoButtonText}>Go</Text>
                    </Pressable>
                  </View>
                )}
                {outreachOwner === 'human' && (
                  <View style={styles.agentActionsRow}>
                    <Text style={styles.agentHelpText}>
                      Log manual outreach attempts.
                    </Text>
                    <Pressable
                      style={[styles.agentGoButton, !canLogHumanAttempt && styles.agentGoButtonDisabled]}
                      onPress={() => openLogAttemptModal(provider)}
                      disabled={!canLogHumanAttempt}
                    >
                      <Text style={styles.agentGoButtonText}>Log Attempt</Text>
                    </Pressable>
                  </View>
                )}
              </View>

              {/* Contact Method Display */}
              {effectiveContactMethod && (
                <View style={styles.contactMethodSection}>
                  <View style={styles.contactMethodInfo}>
                    {showAiSuggestedRow && validSuggestedMethod && (
                      <View style={styles.contactMethodRow}>
                        <Text style={styles.contactMethodLabel}>AI Suggested:</Text>
                        <View style={[styles.contactMethodBadge, { backgroundColor: `${CONTACT_METHOD_CONFIG[validSuggestedMethod].color}15` }]}>
                          {React.createElement(CONTACT_METHOD_CONFIG[validSuggestedMethod].icon, { size: 12, color: CONTACT_METHOD_CONFIG[validSuggestedMethod].color })}
                          <Text style={[styles.contactMethodText, { color: CONTACT_METHOD_CONFIG[validSuggestedMethod].color }]}>
                            {CONTACT_METHOD_CONFIG[validSuggestedMethod].label}
                          </Text>
                        </View>
                      </View>
                    )}
                    <View style={styles.contactMethodRow}>
                      <Text style={styles.contactMethodLabel}>
                        {showAiSuggestedRow ? 'Using:' : 'Contact via:'}
                      </Text>
                      <Pressable
                        style={[styles.contactMethodBadge, { backgroundColor: `${CONTACT_METHOD_CONFIG[effectiveContactMethod].color}15` }]}
                        onPress={() => !disabled && onContactMethodChange && openContactMethodModal(provider.id)}
                        disabled={disabled || !onContactMethodChange}
                      >
                        {React.createElement(CONTACT_METHOD_CONFIG[effectiveContactMethod].icon, {
                          size: 12,
                          color: CONTACT_METHOD_CONFIG[effectiveContactMethod].color,
                        })}
                        <Text style={[styles.contactMethodText, { color: CONTACT_METHOD_CONFIG[effectiveContactMethod].color }]}>
                          {CONTACT_METHOD_CONFIG[effectiveContactMethod].label}
                        </Text>
                        {onContactMethodChange && !disabled && (
                          <ChevronDown size={12} color={CONTACT_METHOD_CONFIG[effectiveContactMethod].color} />
                        )}
                      </Pressable>
                    </View>
                    {provider.contact_method_reason && (
                      <Text style={styles.contactMethodReason}>{provider.contact_method_reason}</Text>
                    )}
                  </View>
                </View>
              )}

              {/* Contact Progress Summary */}
              {hasProgress && (
                <View style={styles.progressSummary}>
                  <Text style={styles.progressSummaryText}>
                    {contactProgress.length} contact attempt{contactProgress.length !== 1 ? 's' : ''}
                  </Text>
                  <View style={styles.progressIcons}>
                    {contactProgress.slice(0, 3).map((attempt, idx) => {
                      const methodKey = getValidContactMethod(attempt.method);
                      const methodConfig = methodKey ? CONTACT_METHOD_CONFIG[methodKey] : CONTACT_METHOD_CONFIG.phone;
                      const MethodIcon = methodConfig.icon;
                      return (
                        <View key={attempt.id || idx} style={styles.progressIcon}>
                          <MethodIcon size={12} color={methodConfig.color} />
                        </View>
                      );
                    })}
                    {contactProgress.length > 3 && (
                      <Text style={styles.moreProgressText}>+{contactProgress.length - 3}</Text>
                    )}
                  </View>
                </View>
              )}

              {/* Details Toggle */}
              <Pressable
                style={styles.detailsToggle}
                onPress={() => setExpandedProvider(expandedProvider === provider.id ? null : provider.id)}
                disabled={disabled}
              >
                <Text style={styles.detailsToggleText}>
                  {expandedProvider === provider.id ? 'Hide' : 'Show'} Contact Details
                </Text>
                {expandedProvider === provider.id ? (
                  <ChevronUp size={16} color="#6B7280" />
                ) : (
                  <ChevronDown size={16} color="#6B7280" />
                )}
              </Pressable>

              {/* Expanded Details */}
              {expandedProvider === provider.id && (
                <View style={styles.detailsContainer}>
                  <Text style={styles.detailsTitle}>Contact Progress</Text>
                  
                  {contactProgress.length > 0 ? (
                    <View style={styles.contactAttemptsList}>
                      {contactProgress.map((attempt, idx) => {
                        const methodKey = getValidContactMethod(attempt.method);
                        const methodConfig = methodKey ? CONTACT_METHOD_CONFIG[methodKey] : CONTACT_METHOD_CONFIG.phone;
                        const MethodIcon = methodConfig.icon;
                        const statusColor = attempt.status === 'success' ? '#16A34A' : 
                                          attempt.status === 'failed' ? '#DC2626' : '#9CA3AF';
                        
                        return (
                          <View key={attempt.id || idx} style={styles.contactAttempt}>
                            <View style={styles.contactAttemptHeader}>
                              <View style={styles.contactAttemptMethod}>
                                <MethodIcon size={14} color={methodConfig.color} />
                                <Text style={styles.contactAttemptMethodText}>
                                  {methodConfig.label}
                                </Text>
                              </View>
                              <View style={[styles.contactAttemptStatus, { backgroundColor: `${statusColor}15` }]}>
                                <Text style={[styles.contactAttemptStatusText, { color: statusColor }]}>
                                  {attempt.status === 'success' ? 'Success' : 
                                   attempt.status === 'failed' ? 'Failed' : 'No Response'}
                                </Text>
                              </View>
                            </View>
                            <Text style={styles.contactAttemptTimestamp}>
                              {new Date(attempt.timestamp).toLocaleString()}
                            </Text>
                            {attempt.notes && (
                              <Text style={styles.contactAttemptNotes}>{attempt.notes}</Text>
                            )}
                          </View>
                        );
                      })}
                    </View>
                  ) : (
                    <View style={styles.noProgress}>
                      <Text style={styles.noProgressText}>
                        No contact attempts yet. Human and AI outreach attempts will appear here.
                      </Text>
                    </View>
                  )}

                  {/* Placeholder for future AI contact actions */}
                  <View style={styles.futureActions}>
                    <Text style={styles.futureActionsTitle}>Future AI Actions</Text>
                    <Text style={styles.futureActionsText}>
                      Outreach activity is logged here for both AI and human contacts.
                    </Text>
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </View>

      {/* Status Selection Modal */}
      <Modal
        visible={statusModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setStatusModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setStatusModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Update Status</Text>
                  <Pressable onPress={() => setStatusModalVisible(false)}>
                    <XCircle size={20} color="#6B7280" />
                  </Pressable>
                </View>
                
                {selectedProvider && (
                  <View style={styles.modalProviderInfo}>
                    <Text style={styles.modalProviderName}>{selectedProvider.name}</Text>
                    <Text style={styles.modalProviderSpecialty}>{selectedProvider.specialty}</Text>
                  </View>
                )}

                <ScrollView style={styles.statusOptions}>
                  {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                    const StatusIcon = config.icon;
                    const isSelected = selectedProvider?.status === status;
                    
                    return (
                      <Pressable
                        key={status}
                        style={[
                          styles.statusOption,
                          isSelected && styles.statusOptionSelected,
                        ]}
                        onPress={() => selectedProviderId && handleStatusChange(selectedProviderId, status as ProviderStatus)}
                      >
                        <View style={[styles.statusOptionIcon, { backgroundColor: `${config.color}15` }]}>
                          <StatusIcon size={18} color={config.color} />
                        </View>
                        <Text style={[styles.statusOptionText, isSelected && styles.statusOptionTextSelected]}>
                          {config.label}
                        </Text>
                        {isSelected && (
                          <View style={styles.statusOptionCheck}>
                            <CheckCircle2 size={18} color={config.color} />
                          </View>
                        )}
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Contact Method Selection Modal */}
      <Modal
        visible={contactMethodModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setContactMethodModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setContactMethodModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Select Contact Method</Text>
                  <Pressable onPress={() => setContactMethodModalVisible(false)}>
                    <XCircle size={20} color="#6B7280" />
                  </Pressable>
                </View>
                
                {selectedContactProvider && (
                  <View style={styles.modalProviderInfo}>
                    <Text style={styles.modalProviderName}>{selectedContactProvider.name}</Text>
                    <Text style={styles.modalProviderSpecialty}>{selectedContactProvider.specialty}</Text>
                    {(() => {
                      const suggested = getValidContactMethod(selectedContactProvider.suggested_contact_method);
                      const contact = getValidContactMethod(selectedContactProvider.contact_method);
                      if (!suggested || !contact || suggested === contact) return null;
                      return (
                        <Text style={styles.modalProviderNote}>
                          AI suggested: {CONTACT_METHOD_CONFIG[suggested].label}
                        </Text>
                      );
                    })()}
                  </View>
                )}

                <ScrollView style={styles.statusOptions}>
                  {Object.entries(CONTACT_METHOD_CONFIG).map(([method, config]) => {
                    const MethodIcon = config.icon;
                    const isSelected = selectedContactProvider?.contact_method === method;
                    const isSuggested = selectedContactProvider?.suggested_contact_method === method;
                    
                    return (
                      <Pressable
                        key={method}
                        style={[
                          styles.statusOption,
                          isSelected && styles.statusOptionSelected,
                        ]}
                        onPress={() => selectedContactProviderId && handleContactMethodChange(selectedContactProviderId, method as ContactMethod)}
                      >
                        <View style={[styles.statusOptionIcon, { backgroundColor: `${config.color}15` }]}>
                          <MethodIcon size={18} color={config.color} />
                        </View>
                        <View style={styles.statusOptionTextContainer}>
                          <Text style={[styles.statusOptionText, isSelected && styles.statusOptionTextSelected]}>
                            {config.label}
                          </Text>
                          {isSuggested && (
                            <Text style={styles.suggestedBadge}>AI Suggested</Text>
                          )}
                        </View>
                        {isSelected && (
                          <View style={styles.statusOptionCheck}>
                            <CheckCircle2 size={18} color={config.color} />
                          </View>
                        )}
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Outreach Owner Selection Modal */}
      <Modal
        visible={outreachOwnerModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setOutreachOwnerModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setOutreachOwnerModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Select Outreach Owner</Text>
                  <Pressable onPress={() => setOutreachOwnerModalVisible(false)}>
                    <XCircle size={20} color="#6B7280" />
                  </Pressable>
                </View>

                {selectedOwnerProvider && (
                  <View style={styles.modalProviderInfo}>
                    <Text style={styles.modalProviderName}>{selectedOwnerProvider.name}</Text>
                    <Text style={styles.modalProviderSpecialty}>{selectedOwnerProvider.specialty}</Text>
                  </View>
                )}

                <ScrollView style={styles.statusOptions}>
                  {Object.entries(OUTREACH_OWNER_CONFIG).map(([owner, config]) => {
                    const isSelected = (selectedOwnerProvider?.outreach_owner || 'human') === owner;
                    return (
                      <Pressable
                        key={owner}
                        style={[
                          styles.statusOption,
                          isSelected && styles.statusOptionSelected,
                        ]}
                        onPress={() => selectedOwnerProviderId && handleOutreachOwnerChange(selectedOwnerProviderId, owner as OutreachOwner)}
                      >
                        <View style={[styles.statusOptionIcon, { backgroundColor: `${config.color}15` }]}>
                          <Text style={[styles.ownerOptionText, { color: config.color }]}>
                            {owner === 'human' ? 'H' : 'AI'}
                          </Text>
                        </View>
                        <Text style={[styles.statusOptionText, isSelected && styles.statusOptionTextSelected]}>
                          {config.label}
                        </Text>
                        {isSelected && (
                          <View style={styles.statusOptionCheck}>
                            <CheckCircle2 size={18} color={config.color} />
                          </View>
                        )}
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Log Human Contact Attempt Modal */}
      <Modal
        visible={logAttemptModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setLogAttemptModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setLogAttemptModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Log Contact Attempt</Text>
                  <Pressable onPress={() => setLogAttemptModalVisible(false)}>
                    <XCircle size={20} color="#6B7280" />
                  </Pressable>
                </View>

                {selectedLogProvider && (
                  <View style={styles.modalProviderInfo}>
                    <Text style={styles.modalProviderName}>{selectedLogProvider.name}</Text>
                    <Text style={styles.modalProviderSpecialty}>{selectedLogProvider.specialty}</Text>
                  </View>
                )}

                <ScrollView style={styles.statusOptions}>
                  <Text style={styles.logSectionTitle}>Contact method</Text>
                  {Object.entries(CONTACT_METHOD_CONFIG).map(([method, config]) => {
                    const MethodIcon = config.icon;
                    const isSelected = logMethod === method;
                    return (
                      <Pressable
                        key={method}
                        style={[styles.statusOption, isSelected && styles.statusOptionSelected]}
                        onPress={() => setLogMethod(method as ContactMethod)}
                      >
                        <View style={[styles.statusOptionIcon, { backgroundColor: `${config.color}15` }]}>
                          <MethodIcon size={18} color={config.color} />
                        </View>
                        <Text style={[styles.statusOptionText, isSelected && styles.statusOptionTextSelected]}>
                          {config.label}
                        </Text>
                      </Pressable>
                    );
                  })}

                  <Text style={styles.logSectionTitle}>Outcome</Text>
                  {[
                    { key: 'success', label: 'Success', color: '#16A34A' },
                    { key: 'failed', label: 'Failed', color: '#DC2626' },
                    { key: 'no_response', label: 'No Response', color: '#6B7280' },
                  ].map((item) => {
                    const isSelected = logStatus === item.key;
                    return (
                      <Pressable
                        key={item.key}
                        style={[styles.statusOption, isSelected && styles.statusOptionSelected]}
                        onPress={() => setLogStatus(item.key as 'success' | 'failed' | 'no_response')}
                      >
                        <View style={[styles.statusOptionIcon, { backgroundColor: `${item.color}15` }]}>
                          <CheckCircle2 size={18} color={item.color} />
                        </View>
                        <Text style={[styles.statusOptionText, isSelected && styles.statusOptionTextSelected]}>
                          {item.label}
                        </Text>
                      </Pressable>
                    );
                  })}

                  <Text style={styles.logSectionTitle}>Notes (optional)</Text>
                  <TextInput
                    style={styles.logNotesInput}
                    multiline
                    numberOfLines={3}
                    value={logNotes}
                    onChangeText={setLogNotes}
                    placeholder="Add any details from the outreach attempt"
                    textAlignVertical="top"
                  />
                </ScrollView>

                <View style={styles.modalFooterActions}>
                  <Pressable
                    style={[styles.modalButton, styles.modalCancelButton]}
                    onPress={() => setLogAttemptModalVisible(false)}
                  >
                    <Text style={styles.modalCancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.modalButton, styles.modalConfirmButton]}
                    onPress={handleSubmitLogAttempt}
                  >
                    <Text style={styles.modalConfirmButtonText}>Save Attempt</Text>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  providersList: {
    gap: 12,
  },
  providerCard: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    gap: 12,
  },
  providerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  providerInfo: {
    flex: 1,
    gap: 4,
  },
  providerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  providerSpecialty: {
    fontSize: 14,
    color: '#6B7280',
  },
  providerReason: {
    fontSize: 12,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  statusButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  statusText: {
    fontSize: 13,
    fontWeight: '500',
  },
  progressSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  progressSummaryText: {
    fontSize: 12,
    color: '#6B7280',
  },
  progressIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  progressIcon: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreProgressText: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  detailsToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  detailsToggleText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6B7280',
  },
  detailsContainer: {
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  detailsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  contactAttemptsList: {
    gap: 8,
  },
  contactAttempt: {
    padding: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    gap: 6,
  },
  contactAttemptHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contactAttemptMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  contactAttemptMethodText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#111827',
  },
  contactAttemptStatus: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  contactAttemptStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  contactAttemptTimestamp: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  contactAttemptNotes: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  noProgress: {
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    alignItems: 'center',
  },
  noProgressText: {
    fontSize: 13,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  futureActions: {
    padding: 12,
    backgroundColor: '#F0FDF4',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  futureActionsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#166534',
    marginBottom: 4,
  },
  futureActionsText: {
    fontSize: 12,
    color: '#15803D',
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
    gap: 8,
  },
  emptyStateText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#6B7280',
  },
  emptyStateSubtext: {
    fontSize: 13,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    ...shadows.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalProviderInfo: {
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalProviderName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  modalProviderSpecialty: {
    fontSize: 13,
    color: '#6B7280',
    marginTop: 4,
  },
  statusOptions: {
    padding: 16,
  },
  logSectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
    marginTop: 8,
  },
  statusOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  statusOptionSelected: {
    backgroundColor: '#F0F9FF',
    borderColor: '#2563EB',
  },
  statusOptionIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusOptionText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
    color: '#111827',
  },
  statusOptionTextSelected: {
    color: '#2563EB',
    fontWeight: '600',
  },
  statusOptionCheck: {
    marginLeft: 'auto',
  },
  statusOptionTextContainer: {
    flex: 1,
    gap: 4,
  },
  suggestedBadge: {
    fontSize: 11,
    color: '#8B5CF6',
    fontWeight: '500',
  },
  ownerOptionText: {
    fontSize: 12,
    fontWeight: '700',
  },
  modalProviderNote: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 4,
  },
  logNotesInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 10,
    fontSize: 13,
    color: '#111827',
    backgroundColor: '#FFFFFF',
    minHeight: 88,
    marginBottom: 8,
  },
  contactMethodSection: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  contactMethodInfo: {
    gap: 6,
  },
  contactMethodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  contactMethodLabel: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
    minWidth: 80,
  },
  contactMethodBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  contactMethodText: {
    fontSize: 12,
    fontWeight: '500',
  },
  contactMethodReason: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginLeft: 88,
  },
  agentActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  agentHelpText: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  agentGoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#2563EB',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  agentGoButtonDisabled: {
    opacity: 0.5,
  },
  agentGoButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalFooterActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 12,
  },
  modalButton: {
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 6,
    minWidth: 96,
    alignItems: 'center',
  },
  modalCancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  modalCancelButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  modalConfirmButton: {
    backgroundColor: '#2563EB',
  },
  modalConfirmButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
